import { tr } from './locale.js'
import { Children, isValidElement, type CSSProperties, type ReactNode } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import { arkmeLiteralMarkdownNodes, arkmeMarkdownBusinessNodes } from '../markdown.js'
import {
  ArkmeRichText, ArkmeMentionText, copyArkmeRichText, type ArkmeMentionClickHandler, type ArkmeMentionClickPredicate,
} from './ArkmeRichText.js'
import type { ArkmeLinkRenderer } from './ArkmeLinkText.js'
import type { ArkmeTimelineMentionTarget } from '../types.js'

function markdownLinkLabel(children: ReactNode): string {
  return Children.toArray(children).map(child => {
    if (typeof child === 'string' || typeof child === 'number') return String(child)
    return isValidElement<{ children?: ReactNode }>(child) ? markdownLinkLabel(child.props.children) : ''
  }).join('')
}

// Shared with the editor; styles are scoped to avoid changing the Harness page.
export const arkmeMarkdownStyles = `
.arkme-markdown { min-width:0; max-width:100%; overflow-wrap:anywhere; line-height:1.6; white-space:normal; }
.arkme-markdown p { margin:0 0 .45em; min-height:1em; }
.arkme-markdown > :last-child { margin-bottom:0; }
.arkme-markdown h1,.arkme-markdown h2,.arkme-markdown h3,.arkme-markdown h4,.arkme-markdown h5,.arkme-markdown h6 { margin:.6em 0 .3em; line-height:1.35; font-weight:650; }
.arkme-markdown h1 { font-size:1.5em; } .arkme-markdown h2 { font-size:1.3em; } .arkme-markdown h3 { font-size:1.15em; }
.arkme-markdown ul,.arkme-markdown ol { padding-left:1.5em; margin:.35em 0; }
.arkme-markdown ul { list-style:disc; } .arkme-markdown ol { list-style:decimal; }
.arkme-markdown li > p { margin:0; }
.arkme-markdown blockquote { margin:.4em 0; padding-left:.8em; border-left:3px solid var(--dsw-alias-border-secondary,#b9c1cb); color:var(--dsw-alias-label-secondary,#68707c); }
.arkme-markdown pre { max-width:100%; overflow-x:auto; padding:.7em; border-radius:6px; background:var(--dsw-alias-background-secondary,rgba(128,128,128,.1)); white-space:pre; overflow-wrap:normal; }
.arkme-markdown code { font-family:ui-monospace,monospace; font-size:.9em; background:var(--dsw-alias-background-secondary,rgba(128,128,128,.1)); border-radius:3px; padding:.08em .2em; }
.arkme-markdown pre code { padding:0; background:none; }
.arkme-markdown .arkme-markdown-table,.arkme-markdown .tableWrapper { max-width:100%; overflow-x:auto; }
.arkme-markdown table { border-collapse:collapse; margin:.4em 0; } .arkme-markdown th,.arkme-markdown td { border:1px solid var(--dsw-alias-border-secondary,#bdc3ca); padding:.3em .55em; min-width:5em; }
.arkme-markdown th { font-weight:600; background:var(--dsw-alias-background-secondary,rgba(128,128,128,.1)); }
.arkme-markdown input[type=checkbox] { margin-right:.5em; vertical-align:middle; }
.arkme-markdown .task-list-item { list-style:none; }
.arkme-markdown ul[data-type=taskList] { padding-left:0; list-style:none; }
.arkme-markdown li[data-type=taskItem],.arkme-markdown ul[data-type=taskList] > li { display:flex; align-items:baseline; gap:.4em; }
.arkme-markdown li[data-type=taskItem] > div,.arkme-markdown ul[data-type=taskList] > li > div { flex:1; min-width:0; }
.arkme-markdown a { color:var(--dsw-alias-state-business-primary,#3964fe); text-decoration:underline; }
.arkme-markdown .ProseMirror { outline:none; min-height:inherit; white-space:pre-wrap; }
.arkme-markdown .ProseMirror > :first-child { margin-top:0; }
`

export function ArkmeMarkdownBody({ text, highlightMentions = true, renderLink, textStyle, onMentionClick, isMentionClickable, renderImage, localArticleImages = false, mentionTargets, readMentionMembers }: {
  text: string
  highlightMentions?: boolean
  renderLink?: ArkmeLinkRenderer
  textStyle?: Pick<CSSProperties, 'fontSize' | 'lineHeight'> | undefined
  onMentionClick?: ArkmeMentionClickHandler
  isMentionClickable?: ArkmeMentionClickPredicate
  renderImage?: ((ref: string, alt: string) => ReactNode) | undefined
  localArticleImages?: boolean
  mentionTargets?: readonly ArkmeTimelineMentionTarget[]
  readMentionMembers?: ReadonlySet<string>
}) {
  const rich = (children: ReactNode) => Children.map(children, child => typeof child === 'string'
    ? <ArkmeRichText
      text={child}
      highlightMentions={highlightMentions}
      highlightTags={false}
      linkLabelMode="raw"
      {...(renderLink === undefined ? {} : { renderLink })}
      {...(onMentionClick === undefined ? {} : { onMentionClick })}
      {...(isMentionClickable === undefined ? {} : { isMentionClickable })}
    /> : child)
  return <div style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }} data-arkme-text-format="markdown" onCopy={copyArkmeRichText}>
    <style>{arkmeMarkdownStyles}</style>
    <div className="arkme-markdown" style={textStyle}>
      <Markdown remarkPlugins={[remarkGfm, remarkBreaks, [arkmeMarkdownBusinessNodes, mentionTargets === undefined ? {} : { mentions: mentionTargets }], [arkmeLiteralMarkdownNodes, { articleImages: Boolean(renderImage), localArticleImages }]]} components={{
        span: ({ children, node }) => {
          const ref = node?.properties['dataArkmeImageRef'] ?? node?.properties['data-arkme-image-ref']
          const alt = String(node?.properties['dataArkmeImageAlt'] ?? node?.properties['data-arkme-image-alt'] ?? '图片')
          if (typeof ref === 'string') return renderImage?.(ref, alt) ?? <span>[{alt || tr("图片")}{tr("：不可用]")}</span>
          const mentionIndex = node?.properties['data-arkme-mention-index'] ?? node?.properties['dataArkmeMentionIndex']
          const mention = typeof mentionIndex === 'number' ? mentionTargets?.[mentionIndex] : undefined
          if (highlightMentions && mention !== undefined) {
            const label = markdownLinkLabel(children)
            return <ArkmeMentionText text={label} mentionTargets={[{ ...mention, startIndex: 0, length: label.length }]}
              {...(readMentionMembers === undefined ? {} : { readMentionMembers })}
              {...(onMentionClick === undefined ? {} : { onMentionClick })}
              {...(isMentionClickable === undefined ? {} : { isMentionClickable })} />
          }
          return (node?.properties['data-arkme-markdown-run'] ?? node?.properties['dataArkmeMarkdownRun']) === 'tag' && highlightMentions
          ? <ArkmeMentionText
            text={String(children)}
            {...(onMentionClick === undefined ? {} : { onMentionClick })}
            {...(isMentionClickable === undefined ? {} : { isMentionClickable })}
          /> : <span>{rich(children)}</span>
        },
        p: ({ children }) => <p>{rich(children)}</p>,
        h1: ({ children }) => <h1>{rich(children)}</h1>, h2: ({ children }) => <h2>{rich(children)}</h2>,
        h3: ({ children }) => <h3>{rich(children)}</h3>, h4: ({ children }) => <h4>{rich(children)}</h4>,
        h5: ({ children }) => <h5>{rich(children)}</h5>, h6: ({ children }) => <h6>{rich(children)}</h6>,
        strong: ({ children }) => <strong>{rich(children)}</strong>, em: ({ children }) => <em>{rich(children)}</em>,
        del: ({ children }) => <del>{rich(children)}</del>, li: ({ children, className }) => <li className={className}>{rich(children)}</li>,
        td: ({ children, style }) => <td style={style}>{rich(children)}</td>, th: ({ children, style }) => <th style={style}>{rich(children)}</th>,
        table: ({ children }) => <div className="arkme-markdown-table"><table>{children}</table></div>,
        a: ({ children, href }) => {
          if (!href) return <span>{children}</span>
          const label = markdownLinkLabel(children)
          return renderLink?.({ href, text: label || href }) ?? <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
        },
        input: ({ checked }) => <input type="checkbox" checked={Boolean(checked)} disabled aria-label={checked ? tr("已完成") : '未完成'} />,
      }}>{text}</Markdown>
    </div>
  </div>
}
